import {
  InfiniteData,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { APIPageResponse } from 'shared/api/CarrierAPIClient';
import {
  LoadboardAPIPageResponse,
  requestJSONLoadboardAPI,
  requestLoadboardAPI,
} from 'shared/api/LoadboardAPIClient';
import { isPostingUnposted, PostingDTO } from './dto/PostingDTO';
import { PostingsCount } from './LoadboardPostingAPI';

export function fetchSavedLoads(page: number, size: number) {
  return requestJSONLoadboardAPI<LoadboardAPIPageResponse<PostingDTO>>([
    'GET /internal/v1/saved-loads{?page,size}',
    { page, size },
  ]).then((response) => {
    // This API ensures all postings are saved
    response.data.forEach((posting) => {
      posting.is_saved = true;
    });

    return response;
  });
}

function saveLoad(loadGuid: string) {
  return requestLoadboardAPI([
    'POST /internal/v1/saved-loads{?load_guid}',
    { load_guid: loadGuid },
  ]);
}

function deleteSavedLoad(loadGuid: string) {
  return requestLoadboardAPI([
    'DELETE /internal/v1/saved-loads{?load_guid}',
    { load_guid: loadGuid },
  ]);
}

function deleteAllSavedLoads() {
  return requestLoadboardAPI('DELETE /internal/v1/saved-loads/all');
}

function deleteUnavailableSavedLoads() {
  return requestLoadboardAPI('DELETE /internal/v1/saved-loads/unavailable');
}

export function useSaveLoadMutation(
  options?: UseMutationOptions<Response, Error, string>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: saveLoad,
    ...options,
    onError(error, loadGuid, onMutateResult, context) {
      updateSavedLoadCache(client, loadGuid, false);
      return options?.onError?.(error, loadGuid, onMutateResult, context);
    },
    onMutate(loadGuid, context) {
      updateSavedLoadCache(client, loadGuid, true);
      return options?.onMutate?.(loadGuid, context);
    },
    onSuccess(...args) {
      invalidateSavedLoads(client);
      return options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteSavedLoadMutation(
  options?: UseMutationOptions<Response, Error, string>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: deleteSavedLoad,
    ...options,
    onError(error, loadGuid, onMutateResult, context) {
      updateSavedLoadCache(client, loadGuid, true);
      return options?.onError?.(error, loadGuid, onMutateResult, context);
    },
    onMutate(loadGuid, context) {
      updateSavedLoadCache(client, loadGuid, false);
      return options?.onMutate?.(loadGuid, context);
    },
  });
}

export function useDeleteUnavailableSavedLoadsMutation(
  options?: UseMutationOptions<Response>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: deleteUnavailableSavedLoads,
    ...options,
    onSuccess(...args) {
      removeAllSavedLoadsFromCache(client, true);
      invalidateSavedLoads(client);
      return options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteAllSavedLoadsMutation(
  options?: UseMutationOptions<Response>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: deleteAllSavedLoads,
    ...options,
    onSuccess(...args) {
      removeAllSavedLoadsFromCache(client);
      invalidateSavedLoads(client);
      return options?.onSuccess?.(...args);
    },
  });
}

function invalidateSavedLoads(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['loadboard', 'loads-count'] });
  void client.invalidateQueries({
    queryKey: ['postings', 'list', { tab: 'saved-loads' }],
    exact: false,
  });
}

function updateSavedLoadCache(
  client: ReturnType<typeof useQueryClient>,
  loadGuid: string,
  isSaved: boolean,
) {
  client.setQueriesData<LoadboardAPIPageResponse<PostingDTO> | undefined>(
    { queryKey: ['postings', 'list'] },
    (prev) => {
      if (!prev) return undefined;

      const data = prev.data.map((posting) => {
        if (posting.load.guid === loadGuid) {
          return { ...posting, is_saved: isSaved };
        }

        return posting;
      });

      return { ...prev, data };
    },
  );

  // Remove saved load from saved-loads tab
  client.setQueriesData<APIPageResponse<PostingDTO> | undefined>(
    { queryKey: ['postings', 'list', { tab: 'saved-loads' }] },
    (prev) => {
      if (!prev) return undefined;

      if (isSaved) {
        const pagination = {
          ...prev.pagination,
          count: prev.pagination.count + 1,
        };
        return { ...prev, pagination };
      }

      const data = prev.data.filter((posting) => {
        return posting.load.guid !== loadGuid;
      });
      const pagination = {
        ...prev.pagination,
        count: prev.pagination.count - 1,
      };

      return { ...prev, data, pagination };
    },
  );

  client.setQueriesData<InfiniteData<APIPageResponse<PostingDTO>> | undefined>(
    { queryKey: ['postings', 'list-infinite'] },
    (prev) => {
      if (!prev) return undefined;

      const pages = prev.pages.map((page) => {
        const data = page.data.map((posting) => {
          if (posting.load.guid === loadGuid) {
            return { ...posting, is_saved: isSaved };
          }

          return posting;
        });
        return { ...page, data };
      });

      return { ...prev, pages };
    },
  );

  client.setQueriesData<PostingDTO | undefined>(
    { queryKey: ['postings', 'item'] },
    (prev) => {
      if (prev?.load.guid === loadGuid) {
        return { ...prev, is_saved: isSaved };
      }

      return prev;
    },
  );

  client.setQueryData<PostingsCount | undefined>(
    ['loadboard', 'loads-count'],
    (prev) => {
      const posting = client.getQueryData<PostingDTO | undefined>([
        'postings',
        'item',
        { guid: loadGuid },
      ]);

      if (!prev || !posting) return prev;
      if (isPostingUnposted(posting)) return prev; // Unposted loads are not counted

      const saved = isSaved ? prev.saved + 1 : prev.saved - 1;
      return { ...prev, saved: Math.max(0, saved) };
    },
  );
}

function removeAllSavedLoadsFromCache(
  client: ReturnType<typeof useQueryClient>,
  onlyUnavailable = false,
) {
  client.setQueriesData<APIPageResponse<PostingDTO> | undefined>(
    { queryKey: ['postings', 'list'] },
    (prev) => {
      if (!prev) return undefined;

      const data = prev.data.map((posting) => {
        if (onlyUnavailable) {
          if (isPostingUnposted(posting)) {
            return { ...posting, is_saved: false };
          }
        } else {
          return { ...posting, is_saved: false };
        }

        return posting;
      });

      return { ...prev, data };
    },
  );

  client.setQueriesData<APIPageResponse<PostingDTO> | undefined>(
    { queryKey: ['postings', 'list', { tab: 'saved-loads' }] },
    (prev) => {
      const counts = client.getQueryData<PostingsCount | undefined>([
        'loadboard',
        'loads-count',
      ]);

      if (!prev || !counts) return undefined;

      if (onlyUnavailable) {
        const data = prev.data.filter((posting) => {
          return !isPostingUnposted(posting);
        });
        const pagination = { ...prev.pagination, count: counts.saved };

        return { ...prev, data, pagination };
      }

      return {
        ...prev,
        data: [],
        pagination: { count: 0, next: undefined, previous: undefined },
      };
    },
  );

  client.setQueriesData<InfiniteData<APIPageResponse<PostingDTO>> | undefined>(
    { queryKey: ['postings', 'list-infinite'] },
    (prev) => {
      if (!prev) return undefined;

      if (onlyUnavailable) {
        const pages = prev.pages.map((page) => {
          const data = page.data.map((posting) => {
            if (isPostingUnposted(posting)) {
              return { ...posting, is_saved: false };
            }

            return posting;
          });
          return { ...page, data };
        });

        return { ...prev, pages };
      }

      const pages = prev.pages.map((page) => {
        const data = page.data.map((posting) => {
          return { ...posting, is_saved: false };
        });
        return { ...page, data };
      });

      return { ...prev, pages };
    },
  );

  client.setQueriesData<PostingDTO | undefined>(
    { queryKey: ['postings', 'item'] },
    (prev) => {
      if (!prev) return undefined;

      if (onlyUnavailable) {
        if (isPostingUnposted(prev)) {
          return prev;
        }
      } else {
        return { ...prev, is_saved: false };
      }

      return prev;
    },
  );

  client.setQueryData<PostingsCount | undefined>(
    ['loadboard', 'loads-count'],
    (prev) => {
      if (!prev) return prev;
      if (onlyUnavailable) return prev;

      return { ...prev, saved: 0 };
    },
  );
}
